/**
 * PriceAlertsPage - Set price alerts for items buyers want
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Bell, Plus, Trash2, DollarSign, Tag, BookOpen,
  Loader2, BellRing, CheckCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'books', label: 'Books' },
  { value: 'magazines', label: 'Magazines' },
  { value: 'educational-media', label: 'Educational Media' },
  { value: 'comics-graphic-novels', label: 'Comics & Graphic Novels' },
  { value: 'cds-dvds', label: 'CDs & DVDs' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'stationery', label: 'Stationery' }
];

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like New' },
  { value: 'good', label: 'Good' },
  { value: 'used', label: 'Used' }
];

// Alert Card Component
const AlertCard = ({ alert, currencySymbol, onDelete }) => (
  <Card className="hover:shadow-md transition-all" data-testid={`alert-card-${alert.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Bell className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h4 className="font-semibold">
              {alert.keywords || CATEGORIES.find(c => c.value === alert.category)?.label}
            </h4>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <Tag className="w-3 h-3" />
              <span>{CATEGORIES.find(c => c.value === alert.category)?.label}</span>
            </div>
          </div>
        </div>
        
        <Button size="sm" variant="ghost" onClick={() => onDelete(alert.id)}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
      
      <div className="mt-4 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Alert when price drops below</p>
          <p className="text-2xl font-bold text-amber-600">
            {currencySymbol}{alert.max_price?.toLocaleString()}
          </p>
        </div>
        
        <div className="text-right">
          {alert.triggered_count > 0 && (
            <Badge className="bg-green-100 text-green-700">
              <BellRing className="w-3 h-3 mr-1" />
              {alert.triggered_count} matches
            </Badge>
          )}
        </div>
      </div>
      
      <div className="mt-3 flex flex-wrap gap-1">
        {alert.condition_acceptable?.map(cond => (
          <Badge key={cond} variant="secondary" className="text-xs">
            {CONDITIONS.find(c => c.value === cond)?.label}
          </Badge>
        ))}
      </div>
    </CardContent>
  </Card>
);

export default function PriceAlertsPage() {
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [email, setEmail] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    category: 'books',
    keywords: '',
    max_price: '',
    condition_acceptable: ['new', 'like_new', 'good']
  });
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  const fetchAlerts = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/books-media/price-alerts?user_email=${email}&country=${country}`);
      const data = await response.json();
      setAlerts(data.alerts || []);
    } catch (err) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };
  
  const createAlert = async () => {
    if (!formData.max_price) {
      toast.error('Please enter a maximum price');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/books-media/price-alerts?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: email,
          user_name: email.split('@')[0],
          category: formData.category,
          keywords: formData.keywords || null,
          max_price: parseFloat(formData.max_price),
          condition_acceptable: formData.condition_acceptable
        })
      });
      
      if (!response.ok) throw new Error('Failed to create alert');
      
      toast.success('Price alert created!');
      setShowCreateModal(false);
      setFormData({
        category: 'books',
        keywords: '',
        max_price: '',
        condition_acceptable: ['new', 'like_new', 'good']
      });
      fetchAlerts();
    } catch (err) {
      toast.error('Failed to create alert');
    }
  };
  
  const deleteAlert = async (alertId) => {
    try {
      await fetch(`${API_URL}/api/books-media/price-alerts/${alertId}?country=${country}`, {
        method: 'DELETE'
      });
      
      toast.success('Alert deleted');
      setAlerts(alerts.filter(a => a.id !== alertId));
    } catch (err) {
      toast.error('Failed to delete alert');
    }
  };
  
  const handleConditionToggle = (condition) => {
    setFormData(prev => ({
      ...prev,
      condition_acceptable: prev.condition_acceptable.includes(condition)
        ? prev.condition_acceptable.filter(c => c !== condition)
        : [...prev.condition_acceptable, condition]
    }));
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-white/20 rounded-xl">
              <Bell className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Price Alerts</h1>
              <p className="text-white/80">Get notified when prices drop below your threshold</p>
            </div>
          </div>
          
          {/* Email Input */}
          <div className="flex gap-3 max-w-md">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white text-gray-900"
              onKeyDown={(e) => e.key === 'Enter' && fetchAlerts()}
            />
            <Button onClick={fetchAlerts} className="bg-white text-orange-600 hover:bg-gray-100">
              View Alerts
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : !searched ? (
          <Card className="text-center py-16">
            <CardContent>
              <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Enter Your Email</h2>
              <p className="text-gray-500">Enter your email above to view and manage your price alerts</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Create Alert Button */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold">Your Price Alerts</h2>
                <p className="text-gray-500">{alerts.length} active alerts</p>
              </div>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Alert
              </Button>
            </div>
            
            {alerts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <BellRing className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Alerts Yet</h3>
                  <p className="text-gray-500 mb-4">Create your first price alert to get notified when prices drop!</p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Alert
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {alerts.map(alert => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    currencySymbol={currencySymbol}
                    onDelete={deleteAlert}
                  />
                ))}
              </div>
            )}
            
            {/* How It Works */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="text-lg">How Price Alerts Work</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                      <Plus className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="font-medium">Set Your Price</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Choose a category and set your maximum price
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="font-medium">We Monitor</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      We check new listings against your alerts
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                      <BellRing className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="font-medium">Get Notified</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Receive instant email when a match is found
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      
      {/* Create Alert Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Price Alert</DialogTitle>
            <DialogDescription>
              Get notified when items matching your criteria are listed below your price
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Keywords (optional)</Label>
              <Input
                placeholder="e.g., Harry Potter, first edition"
                value={formData.keywords}
                onChange={(e) => setFormData({...formData, keywords: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to match all items in category</p>
            </div>
            
            <div>
              <Label>Maximum Price ({currencySymbol}) *</Label>
              <Input
                type="number"
                placeholder="e.g., 5000"
                value={formData.max_price}
                onChange={(e) => setFormData({...formData, max_price: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Acceptable Conditions</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {CONDITIONS.map(cond => (
                  <div key={cond.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`alert-cond-${cond.value}`}
                      checked={formData.condition_acceptable.includes(cond.value)}
                      onCheckedChange={() => handleConditionToggle(cond.value)}
                    />
                    <Label htmlFor={`alert-cond-${cond.value}`} className="font-normal">{cond.label}</Label>
                  </div>
                ))}
              </div>
            </div>
            
            <Alert>
              <Bell className="w-4 h-4" />
              <AlertDescription>
                You'll receive an email notification when matching items are listed below {currencySymbol}{formData.max_price || '0'}
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={createAlert}>
              <Bell className="w-4 h-4 mr-2" />
              Create Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
